/**
 * Customer.io SDK initialization and tracking utilities
 *
 * Initializes the Customer.io CDP Analytics Browser SDK and provides
 * utilities for identifying and tracking users.
 */

import {AnalyticsBrowser} from '@customerio/cdp-analytics-browser';

import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {reporter} from 'common/report/error-reporting';
import {i18next} from 'common/i18n';
import {isPublicMode} from 'common/report/util';

let cioanalytics: AnalyticsBrowser | null = null;

/**
 * Initialize the Customer.io SDK
 * This should be called once at application startup
 */
export function initializeCustomerIO() {
  if (isPublicMode()) {
    return cioanalytics;
  }

  if (cioanalytics) {
    return cioanalytics;
  }

  cioanalytics = AnalyticsBrowser.load(
    {
      writeKey: `575a468a5763a25a2f58`,
    },
    {
      integrations: {
        'Customer.io In-App Plugin': {
          siteId: `45f1c0d079003fa86d54`,
          events: (ev: {type: string; data?: Record<string, any>}) => {
            switch (ev.type) {
              case `in-app:message-opened`:
                mixpanel.track(`[Customer.io] In-App Message Opened`, {
                  'Message ID': ev.data?.messageId,
                  'Delivery ID': ev.data?.deliveryId,
                });
                break;
              case `in-app:message-dismissed`:
                mixpanel.track(`[Customer.io] In-App Message Dismissed`, {
                  'Message ID': ev.data?.messageId,
                  'Delivery ID': ev.data?.deliveryId,
                });
                break;
              case `in-app:message-action`:
                mixpanel.track(`[Customer.io] In-App Message Action`, {
                  'Message ID': ev.data?.messageId,
                  'Delivery ID': ev.data?.deliveryId,
                  'Action Name': ev.data?.actionName,
                  'Action Value': ev.data?.actionValue,
                });
                break;
              case `in-app:message-error`:
                mixpanel.track(`[Customer.io] In-App Message Error`, {
                  'Message ID': ev.data?.messageId,
                  'Delivery ID': ev.data?.deliveryId,
                  Error: ev.data?.error,
                });
                break;
              case `in-app:message-changed`:
                mixpanel.track(`[Customer.io] In-App Message Changed`, {
                  'Message ID': ev.data?.messageId,
                  'Delivery ID': ev.data?.deliveryId,
                });
                break;
            }
          },
        },
      },
    } as any,
  );

  // Track initial page view
  cioanalytics.page();

  return cioanalytics;
}

/**
 * Get the Customer.io analytics instance
 * Returns null if not initialized
 */
export function getCustomerIO() {
  return cioanalytics;
}

/**
 * Identifies the current logged-in user with Customer.io
 *
 * This function should be called after user authentication is confirmed.
 * It retrieves user information from the mp_user cookie and identifies
 * the user with Customer.io using their user ID, email, and name.
 */
export function identifyCustomerIOUser(): void {
  try {
    // Get the Customer.io analytics instance
    const cioanalyticsInstance = getCustomerIO();

    // Check if Customer.io is loaded
    if (!cioanalyticsInstance) {
      reporter.warn(`Customer.io analytics not loaded, skipping identification`);
      return;
    }

    // Get user identity from cookie
    const userIdentity = userCookieUtils.getUserIdentityFromCookie();

    if (!userIdentity) {
      // User is not logged in, skip identification
      return;
    }

    const {id, email, name} = userIdentity;

    // Parse first and last name if available
    const nameParts = name ? name.trim().split(/\s+/) : [];
    const firstName = nameParts.length > 0 ? nameParts[0] : ``;
    const lastName = nameParts.length > 1 ? nameParts.slice(1).join(` `) : ``;

    // Use ready callback to ensure SDK is initialized before identifying
    cioanalyticsInstance.ready(() => {
      cioanalyticsInstance.identify(String(id), {
        email,
        first_name: firstName,
        last_name: lastName,
        language: i18next.language,
      });
    });
  } catch (err) {
    reporter.error(`Error identifying user with Customer.io:`, err);
  }
}
